import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { RailFrame, AdminWindowSwitcher } from '../components/AdminShell';
import {
  FALLBACK_TZ, FLAG_KIND_LABEL, HOUR, axisTicks, fmtDate, fmtDay, fmtMonth, fmtRange, fmtTime,
  laneGroup, localDay, mins, saveTooltip, secs, toMs,
} from '../components/camps/campReviewFormat';
import './CampReviewPage.css';

/* Admin-only review of a past camp, as a full page inside the rail (replaces the v1 modal).
   Reads top to bottom: how the day ran → station throughput → staff workload → who got
   measured → camp-best results → what to double-check. Everything comes from
   GET /api/camps/{id}/review. Stat labels, units and lane groups come from the response's
   `stats`/`stations` (the sport catalog), never from a frontend stat list, and every time is
   shown in the camp's own timezone. */

const SEVERITY_ORDER = { lost: 0, warning: 1, info: 2 };
const SEVERITY_LABEL = { lost: 'Lost', warning: 'Warning', info: 'Info' };
const GROUPS = [
  { key: 'arrival', label: 'Arrival' },
  { key: 'measurables', label: 'Measurables' },
  { key: 'drills', label: 'Drills' },
];
const HALF_HOUR = HOUR / 2;
const PAST = '/camps?when=past';

const num = (v) => (v === null || v === undefined ? '' : String(v));
const statHeader = (s) => (s.unit ? `${s.label} (${s.unit})` : s.label);
const valid = (xs) => xs.filter((n) => !Number.isNaN(n));
const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('');
const spanMin = (startIso, endIso) => Math.round((toMs(endIso) - toMs(startIso)) / 60000);
const plural = (n, one, many) => (n === 1 ? one : many);

function Card({ title, meta, children, testId, className = '' }) {
  return (
    <section className={`adm__card cr2-card ${className}`} data-testid={testId}>
      <div className="cr2-card__head">
        <h2 className="cr2-h2">{title}</h2>
        {meta != null && <span className="cr2-meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

function Avatar({ name, alt, size }) {
  return <span className={`cr2-avatar${alt ? ' cr2-avatar--alt' : ''}${size === 'lg' ? ' cr2-avatar--lg' : ''}`} aria-hidden="true">{initials(name)}</span>;
}

/* "How the day ran": one lane per timeline row, grouped Arrival / Measurables / Drills, on an
   hour-aligned axis that covers every save, the check-in and the scheduled window. */
function Timeline({ review, tz, names, catOf, lostStats }) {
  const { timeline = [], quiet_gaps: gaps = [], summary } = review;
  const rows = timeline.filter((r) => r.start && r.end);

  // Axis bounds: first/last save, the check-in and photo rows (the backend keeps only those
  // inside the session), and the scheduled window. Station rows sit inside first/last.
  const anchors = rows.filter((r) => r.stat === 'check_in' || r.stat === 'photos');
  let points = valid([
    toMs(summary.first), toMs(summary.last),
    ...anchors.flatMap((r) => [toMs(r.start), toMs(r.end)]),
    toMs(summary.scheduled_start), toMs(summary.scheduled_end),
  ]);
  if (Number.isNaN(toMs(summary.first))) points = points.concat(valid(rows.flatMap((r) => [toMs(r.start), toMs(r.end)])));
  if (!rows.length || !points.length) return null;
  const pMin = Math.min(...points);
  const pMax = Math.max(...points);
  const lo = Math.floor(pMin / HOUR) * HOUR;
  const hi = Math.max(lo + HOUR, Math.ceil(pMax / HOUR) * HOUR);
  const span = hi - lo;
  const pct = (ms) => ((ms - lo) / span) * 100;
  const ticks = axisTicks(lo, hi, tz, localDay(pMin, tz) !== localDay(pMax, tz));

  const sStart = toMs(summary.scheduled_start);
  const sEnd = toMs(summary.scheduled_end);
  const hasSched = !Number.isNaN(sStart) && !Number.isNaN(sEnd) && sEnd > sStart;
  const first = toMs(summary.first);
  const last = toMs(summary.last);
  const savesInWindow = hasSched && !(last < sStart || first > sEnd);

  const grouped = GROUPS.map((g) => ({
    ...g, rows: rows.filter((r) => laneGroup({ stat: r.stat, category: catOf[r.stat] }) === g.key),
  })).filter((g) => g.rows.length > 0);

  const barTone = (stat) => {
    if (lostStats.has(stat)) return 'cr2-bar--lost';
    if (stat === 'check_in') return 'cr2-bar--checkin';
    if (stat === 'photos') return 'cr2-bar--photos';
    return 'cr2-bar--station';
  };

  let footer = null;
  if (hasSched && last < sStart) {
    footer = `Every station ran between ${fmtTime(summary.first, tz)} and ${fmtTime(summary.last, tz)}. The whole scheduled window was empty — check the camp's start time or the device clocks.`;
  } else if (hasSched && first > sEnd) {
    footer = `Every station ran between ${fmtTime(summary.first, tz)} and ${fmtTime(summary.last, tz)}, after the scheduled window ended — check the camp's start time or the device clocks.`;
  }

  return (
    <div className="cr2-tl" data-testid="camp-review-timeline">
      <div className="cr2-tl__row cr2-tl__row--axis">
        <div className="cr2-tl__lab" />
        <div className="cr2-tl__track">
          {ticks.map((k) => <i key={k.t} className="cr2-tl__tick" data-testid="cr-tick" style={{ left: `${pct(k.t)}%` }}>{k.label}</i>)}
          {gaps.map((g) => (
            <b key={g.start} className="cr2-tl__gapcap"
              style={{ left: `${(pct(toMs(g.start)) + pct(toMs(g.end))) / 2}%` }}>{`${Math.round(g.minutes)} min quiet`}</b>
          ))}
        </div>
      </div>
      <div className="cr2-tl__body">
        <div className="cr2-tl__bands" aria-hidden="true">
          {ticks.map((k) => <span key={k.t} className="cr2-tl__grid" style={{ left: `${pct(k.t)}%` }} />)}
          {hasSched && (
            <div className="cr2-tl__sched" style={{ left: `${pct(sStart)}%`, width: `${pct(sEnd) - pct(sStart)}%` }}>
              <span className="cr2-tl__schedlab">
                {`Scheduled ${fmtRange(summary.scheduled_start, summary.scheduled_end, tz)}${savesInWindow ? '' : ' — no saves'}`}
              </span>
            </div>
          )}
          {gaps.map((g) => {
            const left = pct(toMs(g.start));
            return (
              <div key={g.start} data-testid="cr-gap" className="cr2-tl__gap"
                title={`No saves ${fmtTime(g.start, tz)} – ${fmtTime(g.end, tz)} (${mins(g.minutes)})`}
                style={{ left: `${left}%`, width: `${Math.max(0.4, pct(toMs(g.end)) - left)}%` }} />
            );
          })}
        </div>
        {grouped.map((g) => (
          <React.Fragment key={g.key}>
            <div className="cr2-tl__group" data-testid="cr2-lane-group">{g.label}</div>
            {g.rows.map((r) => {
              const left = pct(toMs(r.start));
              const width = Math.max(0.5, pct(toMs(r.end)) - left);
              return (
                <div className="cr2-tl__row" key={r.stat} data-testid={`cr2-lane-${r.stat}`}>
                  <div className="cr2-tl__lab" title={r.label}>{r.label}</div>
                  <div className="cr2-tl__track">
                    <div data-testid={`cr-bar-${r.stat}`} title={saveTooltip(r, names, tz)}
                      className={`cr2-bar ${barTone(r.stat)}`} style={{ left: `${left}%`, width: `${width}%` }} />
                    <span className="cr2-tl__dur" style={{ left: `calc(${left + width}% + 8px)` }}>
                      {`${spanMin(r.start, r.end)} min`}
                    </span>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <p className="cr2-note">{footer ? `${footer} ` : ''}Hover a bar for each save.</p>
    </div>
  );
}

export default function CampReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API_URL } = useAuth();
  const [review, setReview] = useState(null);
  const [error, setError] = useState(false);
  const [flagFilter, setFlagFilter] = useState('all');

  useEffect(() => {
    let live = true;
    setReview(null);
    setError(false);
    axios.get(`${API_URL}/camps/${id}/review`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => {
        if (!live) return;
        const d = res && res.data;
        if (d && d.camp && d.summary && d.funnel) setReview(d);
        else setError(true);
      })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [API_URL, id]);

  // Escape goes back to the past camps list, unless the admin is typing somewhere.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const t = e.target;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      navigate(PAST);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const derived = useMemo(() => {
    if (!review) return null;
    const stats = review.stats || [];
    const athletes = review.athletes || [];
    const names = {};
    athletes.forEach((a) => { names[a.id] = a.name; });
    const measured = athletes.filter((a) => a.best && Object.keys(a.best).length > 0);
    const bests = {};
    stats.forEach((s) => {
      const vals = measured.map((a) => a.best[s.key]).filter((v) => typeof v === 'number');
      if (vals.length) bests[s.key] = s.lower_is_better ? Math.min(...vals) : Math.max(...vals);
    });
    const statLabel = {};
    const catOf = {};
    stats.forEach((s) => { statLabel[s.key] = s.label; catOf[s.key] = s.category; });
    (review.stations || []).forEach((s) => {
      if (!statLabel[s.stat]) statLabel[s.stat] = s.label;
      if (catOf[s.stat] == null) catOf[s.stat] = s.category;
    });
    const flags = [...(review.flags || [])].sort(
      (a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3));
    const counts = { lost: 0, warning: 0, info: 0 };
    flags.forEach((f) => { if (f.severity in counts) counts[f.severity] += 1; });
    const flaggedStations = new Set(flags.filter((f) => f.stat && f.severity !== 'info').map((f) => f.stat));
    const lostStats = new Set(flags.filter((f) => f.stat && f.severity === 'lost').map((f) => f.stat));
    // Stations in the order they opened; rows without a start (imported) keep payload order.
    const stations = (review.stations || []).map((s, i) => ({ s, i }))
      .sort((a, b) => {
        const ta = toMs(a.s.start); const tb = toMs(b.s.start);
        if (Number.isNaN(ta) || Number.isNaN(tb)) return Number.isNaN(ta) - Number.isNaN(tb) || a.i - b.i;
        return ta - tb || a.i - b.i;
      }).map((x) => x.s);
    const spans = measured.map((a) => a.span_minutes).filter((v) => typeof v === 'number');
    const staffTotal = (review.staff || []).reduce((n, p) => n + (p.entries || 0), 0);
    // Results: no column for a stat nobody at this camp has a best for.
    const tableStats = stats.filter((s) => s.key in bests);
    const unmeasured = athletes.filter((a) => a.registered && !measured.includes(a)).length;
    return { tableStats, names, measured, bests, statLabel, catOf, flags, counts, flaggedStations,
      lostStats, stations, spans, staffTotal, unmeasured };
  }, [review]);

  const crumbs = (name) => (
    <nav className="adm-crumbs" aria-label="Breadcrumb">
      <Link to="/camps" className="adm-crumbs__link">Camps</Link>
      <span className="adm-crumbs__sep" aria-hidden="true">›</span>
      <Link to={PAST} className="adm-crumbs__link">Past</Link>
      <span className="adm-crumbs__sep" aria-hidden="true">›</span>
      <span className="adm-crumbs__last" aria-current="page">{name}</span>
    </nav>
  );

  if (error) {
    return (
      <RailFrame active="camps">
        <div className="cr2">
          {crumbs('Review')}
          <p className="adm-error" role="alert">Could not load the camp review.</p>
        </div>
      </RailFrame>
    );
  }
  if (!review) {
    return (
      <RailFrame active="camps">
        <div className="cr2">
          {crumbs('Review')}
          <p className="adm-loading">Loading review…</p>
        </div>
      </RailFrame>
    );
  }

  const { camp, summary, funnel, imported } = review;
  const tz = camp.timezone || FALLBACK_TZ;
  const { tableStats, names, measured, bests, statLabel, catOf, flags, counts, flaggedStations,
    lostStats, stations, spans, staffTotal, unmeasured } = derived;
  const empty = !imported && !summary.entries;
  const hasStats = summary.entries > 0;
  const timing = !imported && !!summary.first;

  // Header
  const dateIso = camp.date || camp.scheduled_start || summary.first;
  const sport = (camp.sport_slug || '').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
  const kindLabel = camp.event_type === 'clinic' ? 'Clinic' : 'Camp';
  const subLine = [fmtDate(dateIso, tz), [camp.venue, camp.location].filter(Boolean).join(', '), sport, kindLabel]
    .filter((x) => x && x !== '—').join(' · ');
  const month = fmtMonth(dateIso, tz);

  // KPIs
  const pctMeasured = summary.registered ? Math.round((summary.measured / summary.registered) * 100) : 0;
  const finished = funnel.finished_all === summary.measured && summary.measured > 0
    ? `all ${summary.measured} finished every station`
    : `${funnel.finished_all} finished every station`;
  const offset = summary.start_offset_minutes;
  let offsetText = null;
  if (summary.scheduled_start && offset !== null && offset !== undefined) {
    const r = Math.round(offset);
    offsetText = r === 0 ? 'on time' : `${Math.abs(r)} min ${r < 0 ? 'early' : 'late'}`;
  }
  const fastest = spans.length ? Math.min(...spans) : null;
  const perSub = [
    summary.median_athlete_minutes != null && `median ${Math.round(summary.median_athlete_minutes)}`,
    fastest != null && `fastest ${Math.round(fastest)}`,
    summary.athletes_per_hour != null && `${summary.athletes_per_hour} athletes / hr`,
  ].filter(Boolean).join(' · ');

  // Station strips sit on an axis zoomed to the measuring window.
  const zLo = Math.floor(toMs(summary.first) / HOUR) * HOUR;
  const zHi = Math.max(zLo + HALF_HOUR, Math.ceil(toMs(summary.last) / HALF_HOUR) * HALF_HOUR);
  const zPct = (ms) => ((ms - zLo) / (zHi - zLo)) * 100;
  const zIso = (ms) => new Date(ms).toISOString();

  const staffColors = ['cr2-fill--green', 'cr2-fill--slate', 'cr2-fill--light', 'cr2-fill--amber'];
  const timeScale = Math.max(70, Math.ceil(spans.length ? Math.max(...spans) : 0));
  const avg = summary.avg_athlete_minutes;
  const tight = spans.length > 1 && Math.max(...spans) - Math.min(...spans) <= 10;

  const funnelRows = [
    { label: 'registered', value: funnel.registered, tone: 'pale' },
    { label: 'checked in', value: funnel.checked_in, tone: 'green' },
    { label: 'got at least one stat', value: funnel.measured, tone: 'green' },
    { label: 'finished every station', value: funnel.finished_all, tone: 'green' },
    { label: funnel.photos_lower_bound ? 'have camp photos (at least)' : 'have camp photos',
      value: funnel.photos, shown: funnel.photos_lower_bound ? `${funnel.photos}+` : funnel.photos, tone: 'light' },
  ];

  const shownFlags = flagFilter === 'all' ? flags : flags.filter((f) => f.severity === flagFilter);
  const filterOpts = [
    { key: 'all', label: `All ${flags.length}` },
    { key: 'lost', label: `Lost ${counts.lost}` },
    { key: 'warning', label: `Warnings ${counts.warning}` },
    { key: 'info', label: `Info ${counts.info}` },
  ];

  const goCamps = (open) => navigate(`${PAST}&open=${open}:${camp.id}`);

  return (
    <RailFrame active="camps">
      <div className="cr2">
        <div>
          {crumbs(camp.name)}
          <header className="cr2-head">
            <div className="cr2-head__left">
              {month !== '—' && (
                <span className="cr2-datetile" data-testid="cr2-datetile" aria-hidden="true">
                  <span className="cr2-datetile__m">{month}</span>
                  <span className="cr2-datetile__d">{fmtDay(dateIso, tz)}</span>
                </span>
              )}
              <div>
                <h1 className="adm-header__title">{camp.name}</h1>
                <p className="adm-header__sub" data-testid="cr2-sub">{subLine}</p>
              </div>
            </div>
            <AdminWindowSwitcher label="Camp pages" value="review"
              onChange={(v) => { if (v !== 'review') goCamps(v); }}
              options={[{ value: 'review', label: 'Review' }, { value: 'registrations', label: 'Registrations' },
                { value: 'edit', label: 'Edit' }]} />
          </header>
        </div>

        {!empty && (
          <div className="adm-kpis cr2-kpis">
            {timing && (
              <div className="adm-kpi adm-kpi--hero">
                <span className="adm-kpi__label">Measuring ran</span>
                <div className="adm-kpi__value cr2-kpi__range">{`${fmtTime(summary.first, tz)} – ${fmtTime(summary.last, tz)}`}</div>
                <div className="adm-kpi__sub cr2-kpi__sub--onhero">
                  {mins(summary.duration_minutes)}
                  {summary.scheduled_start && ` · scheduled ${fmtRange(summary.scheduled_start, summary.scheduled_end, tz)}`}
                  {offsetText && <> · <b>{offsetText}</b></>}
                </div>
              </div>
            )}
            <div className="adm__card adm-kpi">
              <span className="adm-kpi__label adm-kpi__label--dark">Measured</span>
              <div className="adm-kpi__value adm-kpi__value--dark">
                {summary.measured}<span className="cr2-kpi__of">{` / ${summary.registered}`}</span>
              </div>
              <div className="cr2-track cr2-track--kpi"><span className="cr2-fill cr2-fill--green" style={{ width: `${Math.min(100, pctMeasured)}%` }} /></div>
              <div className="adm-kpi__sub">{`${pctMeasured}% of registered · ${finished}`}</div>
            </div>
            {timing && avg != null && (
              <div className="adm__card adm-kpi">
                <span className="adm-kpi__label adm-kpi__label--dark">Per athlete</span>
                <div className="adm-kpi__value adm-kpi__value--dark">
                  {Math.round(avg)}<span className="cr2-kpi__of"> min</span>
                </div>
                <div className="adm-kpi__sub">{perSub}</div>
              </div>
            )}
            <div className="adm__card adm-kpi">
              <span className="adm-kpi__label adm-kpi__label--dark">Flags</span>
              <div className={`adm-kpi__value adm-kpi__value--dark${flags.length ? ' cr2-red' : ''}`}>{flags.length}</div>
              <div className="adm-kpi__sub cr2-chips">
                {flags.length === 0 && 'Nothing flagged'}
                {counts.lost > 0 && <span className="adm-chip adm-chip--red">{`${counts.lost} lost`}</span>}
                {counts.warning > 0 && <span className="adm-chip adm-chip--amber">{`${counts.warning} warn`}</span>}
                {counts.info > 0 && <span className="adm-chip">{`${counts.info} info`}</span>}
              </div>
            </div>
          </div>
        )}

        {imported && <p className="cr2-banner">No timing data — this camp's results were imported</p>}
        {empty && <p className="cr2-banner">No stats were recorded for this camp</p>}

        {timing && hasStats && (review.timeline || []).some((r) => r.start && r.end) && (
          <section className="adm__card cr2-card">
            <div className="cr2-card__head">
              <h2 className="cr2-h2">How the day ran</h2>
              <span className="cr2-legend">
                <span><i className="cr2-sw cr2-sw--active" />Station active</span>
                <span><i className="cr2-sw cr2-sw--quiet" />Nothing saved</span>
                <span><i className="cr2-sw cr2-sw--sched" />Scheduled</span>
              </span>
            </div>
            <Timeline review={review} tz={tz} names={names} catOf={catOf} lostStats={lostStats} />
          </section>
        )}

        {hasStats && stations.length > 0 && (
          <section className="cr2-stations">
            <div className="cr2-sechead">
              <h2 className="cr2-h2">Stations <span className="cr2-h2__suffix">· in the order they opened</span></h2>
              {timing && (
                <span className="cr2-meta">
                  {`Strip shows when the station was active between ${fmtTime(zIso(zLo), tz)} and ${fmtTime(zIso(zHi), tz)}`}
                </span>
              )}
            </div>
            <div className="cr2-stgrid">
              {stations.map((s) => {
                const flagged = flaggedStations.has(s.stat);
                const repeats = Math.max(0, (s.entries || 0) - (s.athletes || 0));
                const hasTime = timing && s.start && s.end;
                const left = hasTime ? Math.max(0, zPct(toMs(s.start))) : 0;
                const width = hasTime ? Math.max(2, zPct(toMs(s.end)) - left) : 0;
                const tone = lostStats.has(s.stat) ? 'lost' : s.stat === 'photos' ? 'photos' : 'station';
                const row = (review.timeline || []).find((r) => r.stat === s.stat);
                const hasBest = s.best !== null && s.best !== undefined;
                return (
                  <div key={s.stat} className={`cr2-st${flagged ? ' cr2-st--flagged' : ''}`} data-testid={`cr2-station-${s.stat}`}>
                    <div className="cr2-st__title">
                      <span>{s.label}</span>
                      {flagged && <span className="cr2-flagpill">FLAG</span>}
                    </div>
                    {hasTime && (
                      <>
                        <div className="cr2-strip" title={row ? saveTooltip(row, names, tz) : s.label}>
                          <span className={`cr2-strip__seg cr2-bar--${tone}`} style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }} />
                        </div>
                        <div className="cr2-st__when">{`${fmtRange(s.start, s.end, tz)} · ${spanMin(s.start, s.end)} min`}</div>
                      </>
                    )}
                    <div className="cr2-st__nums">
                      <div>
                        <b>{s.athletes}<small>{`/${summary.measured}`}</small></b>
                        <span>athletes</span>
                      </div>
                      <div>
                        <b>{s.entries}</b>
                        <span>{repeats > 0 ? `saves (+${repeats} repeats)` : 'saves'}</span>
                      </div>
                    </div>
                    <div className="cr2-st__foot">
                      <span>{hasTime ? `${secs(s.seconds_per_athlete)} / athlete` : ''}</span>
                      {hasBest
                        ? <b className={flagged ? 'cr2-amber' : 'cr2-green'}>{`best ${s.best}${s.unit ? ` ${s.unit}` : ''}`}</b>
                        : <span className="cr2-muted">—</span>}
                    </div>
                    {(s.staff || []).map((p, i) => (
                      <div key={p.id} className="cr2-st__staff">
                        <Avatar name={p.name} alt={i % 2 === 1} />
                        <span>{p.name}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="cr2-three">
          {hasStats && (review.staff || []).length > 0 && (
            <Card title="Staff" testId="cr2-staff"
              meta={`${review.staff.length} entering stats · ${staffTotal} entries`}>
              <div className="cr2-staff">
                {review.staff.map((p, i) => {
                  const share = staffTotal ? Math.round((p.entries / staffTotal) * 100) : 0;
                  return (
                    <div key={p.id} className="cr2-staff__row">
                      <div className="cr2-staff__top">
                        <Avatar name={p.name} alt={i % 2 === 1} size="lg" />
                        <span className="cr2-staff__name">{p.name}</span>
                        <span className="cr2-staff__share"><b>{`${share}%`}</b>{` · ${p.entries} entries`}</span>
                      </div>
                      <div className="cr2-track cr2-track--8">
                        <span className={`cr2-fill ${staffColors[i % staffColors.length]}`} style={{ width: `${share}%` }} />
                      </div>
                      <div className="cr2-staff__sub">
                        {[!imported && p.first ? fmtRange(p.first, p.last, tz) : null,
                          (p.stations || []).map((k) => statLabel[k] || k).join(', ')].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {timing && spans.length > 0 && (
            <Card title="Time per athlete" testId="cr2-time"
              meta={`avg ${Math.round(avg ?? 0)} · median ${Math.round(summary.median_athlete_minutes ?? 0)} min`}>
              <div className="cr2-time">
                {measured.filter((a) => typeof a.span_minutes === 'number').map((a) => (
                  <div key={a.id} className="cr2-time__row">
                    <span className="cr2-time__name" title={a.name}>{a.name}</span>
                    <span className="cr2-track cr2-track--8 cr2-time__track">
                      <span className="cr2-fill cr2-fill--green" style={{ width: `${Math.min(100, (a.span_minutes / timeScale) * 100)}%` }} />
                      {avg != null && <span className="cr2-time__avg" style={{ left: `${Math.min(100, (avg / timeScale) * 100)}%` }} />}
                    </span>
                    <span className="cr2-time__val">{`${Math.round(a.span_minutes)} m`}</span>
                  </div>
                ))}
              </div>
              <p className="cr2-note">
                Dashed line = average.{tight ? ' Tight spread: athletes moved through as one group.' : ''}
              </p>
            </Card>
          )}

          <Card title="Attendance" testId="camp-review-funnel" meta={`of ${funnel.registered} registered`}>
            <div className="cr2-funnel">
              {funnelRows.map((r) => (
                <div key={r.label} className="cr2-funnel__row">
                  <div>
                    <span className="cr2-funnel__label">{r.label}</span>
                    <div className="cr2-track cr2-track--7">
                      <span className={`cr2-fill cr2-fill--${r.tone}`}
                        style={{ width: `${funnel.registered ? Math.max(3, Math.min(100, (r.value / funnel.registered) * 100)) : 3}%` }} />
                    </div>
                  </div>
                  <b className="cr2-funnel__n">{r.shown ?? r.value}</b>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {(!empty || flags.length > 0) && (
          <section className="adm__card adm__card--flush cr2-card">
            <div className="cr2-card__head cr2-card__head--pad">
              <h2 className="cr2-h2">Flags</h2>
              {flags.length > 0 && (
                <div className="adm-window cr2-filter" role="group" aria-label="Filter flags">
                  {filterOpts.map((o) => (
                    <button key={o.key} type="button"
                      className={`adm-window__btn cr2-filter__btn cr2-filter__btn--${o.key}${flagFilter === o.key ? ' adm-window__btn--active' : ''}`}
                      onClick={() => setFlagFilter(o.key)}>{o.label}</button>
                  ))}
                </div>
              )}
            </div>
            {flags.length === 0 ? <p className="adm-empty">Nothing flagged.</p> : (
              <ul className="cr2-flags" data-testid="camp-review-flags">
                {shownFlags.map((f, i) => (
                  <li key={`${f.kind}-${f.stat}-${f.athlete_id}-${i}`} className="cr2-flag"
                    data-testid="camp-review-flag" data-severity={f.severity}>
                    <span className={`cr2-pill cr2-pill--${f.severity}`}>{SEVERITY_LABEL[f.severity] || f.severity}</span>
                    <div>
                      <div className="cr2-flag__top">
                        <b>{f.title}</b>
                        {FLAG_KIND_LABEL[f.kind] && <span className="cr2-flag__kind">{FLAG_KIND_LABEL[f.kind]}</span>}
                      </div>
                      {f.detail && <p className="cr2-flag__detail">{f.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {hasStats && tableStats.length > 0 && (
          <section className="adm__card adm__card--flush cr2-card">
            <div className="cr2-card__head cr2-card__head--pad">
              <h2 className="cr2-h2">Results <span className="cr2-h2__suffix">{`· ${measured.length} ${plural(measured.length, 'athlete', 'athletes')}`}</span></h2>
              <span className="cr2-meta">
                <b className="cr2-green">Green</b> = camp best · amber cell = flagged
                {unmeasured > 0 && ` · ${unmeasured} registered ${plural(unmeasured, 'was', 'were')} not measured`}
              </span>
            </div>
            <div className="adm-tablewrap">
              <table className="adm-table cr2-results" data-testid="camp-review-athletes">
                <thead>
                  <tr>
                    <th>Athlete</th>
                    {tableStats.map((s) => <th key={s.key} className="cr2-n">{statHeader(s)}</th>)}
                    <th className="cr2-n">Attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {measured.map((a) => {
                    const flagged = new Set(a.flagged_stats || []);
                    return (
                      <tr key={a.id}>
                        <td className="cr2-results__name">{a.name}</td>
                        {tableStats.map((s) => {
                          const v = a.best[s.key];
                          const best = typeof v === 'number' && v === bests[s.key];
                          const cls = ['cr2-val', best ? 'cr2-best' : '', flagged.has(s.key) ? 'cr2-hi' : ''].filter(Boolean).join(' ');
                          return (
                            <td key={s.key} className="cr2-n">
                              {v === undefined || v === null ? '' : <span className={cls}>{num(v)}</span>}
                            </td>
                          );
                        })}
                        <td className="cr2-n">{num(a.attempts)}</td>
                      </tr>
                    );
                  })}
                  <tr className="cr2-results__best">
                    <td>Camp best</td>
                    {tableStats.map((s) => <td key={s.key} className="cr2-n"><span className="cr2-val cr2-best">{num(bests[s.key])}</span></td>)}
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {(review.notes || []).length > 0 && (
          <ul className="cr2-notes">
            {review.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </div>
    </RailFrame>
  );
}
